import {
	type Ballot,
	type BallotPageSizeNames,
	type BallotVote,
	type BallotVoteDL38,
	type BallotVoteEW19,
	type BallotVoteGO8,
	type BallotVoteSW23,
	type BallotVoteVEW20,
	type BallotVoteYN,
	renderBallotCandidateName
} from "@/lib/stimmzettel/BallotFactory/Ballot";
import type { BallotRenderer, RenderedBallot } from "@/lib/stimmzettel/BallotFactory/BallotRenderer";
import type { PdfDocument } from "@/lib/pdflib2/PdfDocument";
import type { PdfFont } from "@/lib/pdflib2/PdfFont";
import type { PdfImage } from "@/lib/pdflib2/PdfImage";
import { type PdfBufferedVirtualWriter, pdfVirtualWriter } from "@/lib/pdflib2/PdfBufferedVirtualWriter";
import { objectGroup, type PdfObjectGroup } from "@/lib/pdflib2/PdfObjectGroup";
import { root, vector2d } from "@/lib/pdflib2/Vector2d";
import { centeredText, multilineText, rightAlignedText } from "@/lib/pdflib2/PdfText";
import { PdfGeometries, type PdfPageGeometry } from "@/lib/pdflib2/PdfGeometries";
import { color } from "@/lib/pdflib2/PdfColor";
import type { TextProvider } from "@/lib/abschnittc/TextProvider";
import { strokeRect } from "@/lib/pdflib2/PdfRectangle";
import debug from "debug";
import { whitespace } from "@/lib/pdflib2/PdfWhitespace";
import type { Resolvable } from "@/lib/TextProvider";
import { cross, line } from "@/lib/pdflib2/PdfLine";
import type { BallotCandidate } from "@/lib/stimmzettel/BallotFactory/BallotCandidate";
import type { PdfDocumentManager } from "@/lib/pdflib2/PdfDocumentManager";

const logger = debug("stimmzettel:BallotRendererImpl");

export interface BallotRendererImplOptions {
	pdfDocumentManager: PdfDocumentManager;
	textProvider: TextProvider;
}

interface VoteBodyArgs<VoteType extends BallotVote> {
	vote: VoteType,
	writer: PdfBufferedVirtualWriter,
	assets: {
		regular: PdfFont,
		italic: PdfFont,
		bold: PdfFont,
		logo: PdfImage
	}
}

interface RenderSingleBallotArgs {
	writer: PdfBufferedVirtualWriter;
	assets: {
		regular: PdfFont;
		italic: PdfFont;
		bold: PdfFont;
		logo: PdfImage;
	};
	ballot: Ballot;
}

export class BallotRendererImpl implements BallotRenderer {

	constructor(private options: BallotRendererImplOptions) {
	}

	async renderToPdf(ballot: Ballot): Promise<RenderedBallot> {
		return {
			ballot: await this.renderBallotPage(ballot),
			resultSheet: await this.renderResultSheetPage(ballot)
		};
	}

	async addAssets(document: PdfDocument): Promise<{
		regular: PdfFont;
		italic: PdfFont;
		bold: PdfFont;
		logo: PdfImage;
	}> {

		const UbuntuR = (await import("@/assets/fonts/Ubuntu-R.ttf?arraybuffer")).default;
		const UbuntuRI = (await import("@/assets/fonts/Ubuntu-RI.ttf?arraybuffer")).default;
		const UbuntuB = (await import("@/assets/fonts/Ubuntu-B.ttf?arraybuffer")).default;
		const VoltLogo = (await import("@/assets/img/logo-dark.png?arraybuffer")).default;

		return {
			regular: await document.addFont(UbuntuR),
			italic: await document.addFont(UbuntuRI),
			bold: await document.addFont(UbuntuB),
			logo: await document.addImage(VoltLogo, "png")
		};
	}

	static toGeometry(pageSize: BallotPageSizeNames): PdfPageGeometry {
		switch (pageSize) {
			case "A4_2":
				return PdfGeometries.A4L_2;
			case "A4":
				return PdfGeometries.A4;
			case "2_A4":
				return PdfGeometries["2_A4L"];
			case "A5":
				return PdfGeometries.A5;
			case "4_A4":
				return PdfGeometries["4_A4"];
			case "4_A4L":
				return PdfGeometries["4_A4L"];
			case "A3_2":
				return PdfGeometries.A3L_2;
			case "A3_3":
				return PdfGeometries.A3L_3;
			default:
				throw new Error(`Unknown page size ${pageSize}`);
		}
	}

	static resolveVoteArguments(vote: BallotVote, res: Resolvable) {

		res.set("what", vote.what);

		switch (vote.type) {
			case "sw23":
				res.set("maxPoints", vote.maxPoints);
				break;
			case "vew20":
				res.set("voteCount", vote.voteCount);
				break;
		}
	}

	resolveText(vote: BallotVote, key: string): string {
		const group = this.options.textProvider.group("ballot");

		const unresolved = group.sub(vote.type).get(key);
		BallotRendererImpl.resolveVoteArguments(vote, unresolved);
		return unresolved.get();
	}

	async renderBallotPage(ballot: Ballot): Promise<ArrayBuffer> {

		const document = await this.options.pdfDocumentManager.create();
		const assets = await this.addAssets(document);
		const geometry = BallotRendererImpl.toGeometry(ballot.paperSize);

		const writer = pdfVirtualWriter(
			geometry.padding(7, 7, 7, 7)
		);

		this.renderSingleBallot({
			assets, writer, ballot
		});

		writer.complete(document);

		return await this.options.pdfDocumentManager.exportAsArrayBuffer(document);
	}

	renderSingleBallot(args: RenderSingleBallotArgs): void {
		const { writer, assets, ballot } = args;
		const { black, lightGray } = this.colors();
		const fontSizes = this.fontSizes();
		const secret = ballot.secret;

		// draw header
		const maxWidth = writer.safeGeometryWidth();

		const tg = this.options.textProvider.group("ballot");

		// Stimmzettel  [ zk-identifier ] [ logo ]
		// [Event Name] [                ] [ secret ]

		const gap = 5;
		const zkWidth = maxWidth * 0.15;
		const zkHeight = 0.43 * zkWidth;
		const titleWidth = (0.70 * maxWidth) - gap - zkWidth / 2;
		const titleOffset = 0;
		const zkOffset = titleOffset + titleWidth + gap;
		const secretWidth = (0.30 * maxWidth) - gap - zkWidth / 2;
		const secretOffset = zkOffset + zkWidth + gap;

		logger(`max: ${maxWidth}. ${titleOffset}->${titleWidth} | ${gap}->${zkOffset} | ${zkWidth}+${gap}->${secretOffset} | ${secretWidth}->${maxWidth}`);

		const titleGroup = objectGroup()
			.addFlow(multilineText(ballot.eventName, assets.regular.atSize(fontSizes.subTitle), black, titleWidth))
			.addFlow(whitespace(vector2d(0, 2.5)))
			.addFlow(multilineText(tg.get("title").get(), assets.bold.atSize(fontSizes.title), black, titleWidth))
			.addFlow(whitespace(vector2d(0, 5)));

		const zkGroup = objectGroup()
			.addFlow(strokeRect(vector2d(zkWidth, zkHeight), 1, black))
			.addRelative(centeredText("Feld nicht beschriften", assets.italic.atSize(zkHeight / 2), lightGray, vector2d(zkWidth, zkHeight - 1)), vector2d(0, 0));

		const voltHeaderLogo = assets.logo.atWidth(secretWidth);
		const secretGroup = objectGroup()
			.addFlow(voltHeaderLogo)
			.addFlow(rightAlignedText(secret, assets.bold.atSize(9), black, voltHeaderLogo.size().width()));

		const headerGroup = objectGroup()
			.addRelative(titleGroup, vector2d(titleOffset, 0))
			.addRelative(zkGroup, vector2d(zkOffset, 0))
			.addRelative(secretGroup, vector2d(secretOffset, 0));

		writer.addObject(headerGroup);

		for (let i = 0; i < ballot.votes.length; i++) {
			const vote = ballot.votes[i];
			const isLast = i >= ballot.votes.length - 1;
			const sectionTitle = vote.what;

			const explainerText = this.resolveText(vote, "explainer");
			const instructionText = this.resolveText(vote, "instruction");

			const title = ballot.votes.length > 1 ? `${i + 1}. ${sectionTitle}` : sectionTitle;

			const voteTitle = objectGroup()
				.addFlow(multilineText(title, assets.bold.atSize(fontSizes.subTitle), black, maxWidth))
				.addFlow(whitespace(vector2d(0, 2.5)))
				.addFlow(multilineText(explainerText, assets.regular.atSize(fontSizes.explainer), black, maxWidth))
				.addFlow(whitespace(vector2d(0, 2.5)))
				.addFlow(multilineText(instructionText, assets.italic.atSize(fontSizes.instruction), black, maxWidth))
				.addFlow(whitespace(vector2d(0, 5)));

			const voteBody = this.voteBody({
				vote, writer, assets
			});

			writer
				.addObject(voteTitle)
				.addObjects(voteBody);

			if (!isLast)
				writer.addObject(objectGroup()
					.addFlow(line(vector2d(0, 0), vector2d(maxWidth, 0), lightGray, 1))
					.addFlow(whitespace(vector2d(0, 4))));
		}
	}

	voteBodyEW19(args: VoteBodyArgs<BallotVoteEW19>): PdfObjectGroup[] {
		const { vote, writer, assets } = args;
		const fontSizes = this.fontSizes();
		const maxWidth = writer.safeGeometryWidth();
		const { black } = this.colors();

		const candidates = this.sortCandidates(vote.candidates);

		const out: PdfObjectGroup[] = [];

		const boxSize = 4;
		const boxGap = 5;

		for (const candidate of candidates) {
			out.push(objectGroup()
				.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(boxGap, 0.7))
				.addRelative(multilineText(renderBallotCandidateName(candidate), assets.regular.atSize(fontSizes.candidate), black, maxWidth),
					vector2d(boxGap * 2 + boxSize, 0))
				.addFlow(whitespace(vector2d(0, boxSize + boxGap))));
		}

		return out;
	}

	sortCandidates(input: BallotCandidate[]): BallotCandidate[] {
		return this.sortCandidatesWithMinSpot(input.map(value => ({ ...value, minSpot: 1 })));
	}

	colors() {
		return {
			black: color(0, 0, 0),
			lightGray: color(0.8, 0.8, 0.8)
		};
	}

	sortCandidatesWithMinSpot(input: BallotCandidate[]): BallotCandidate[] {
		return input.sort((a: BallotCandidate, b: BallotCandidate) => {

			const aSpot = a.minSpot ?? 1;
			const bSpot = b.minSpot ?? 1;

			if (aSpot !== bSpot)
				return aSpot - bSpot;

			if (a.lastName !== b.lastName)
				return a.lastName.localeCompare(b.lastName);

			if (a.firstName !== b.firstName)
				return a.firstName.localeCompare(b.firstName);

			if (a.extra !== b.extra) {
				if (!a.extra)
					return -1;
				if (!b.extra)
					return 1;
				return a.extra.localeCompare(b.extra);
			} else {
				return 0;
			}
		});
	}

	voteBodySW23(args: VoteBodyArgs<BallotVoteSW23>): PdfObjectGroup[] {

		const fontSizes = this.fontSizes();

		const { vote, writer, assets } = args;
		const maxWidth = writer.safeGeometryWidth();
		const { black } = this.colors();
		const textGroup = this.options.textProvider.group("ballot").sub("sw23");

		const candidateWidth = maxWidth * (1 / 3);
		const pointsWidth = candidateWidth * 2;
		const boxWidth = 4;
		const candidates = this.sortCandidatesWithMinSpot(vote.candidates);
		let lastMinSpot = 0;

		const groups: PdfObjectGroup[] = [];

		for (let i = 0; i < candidates.length; i++) {

			const candidate = candidates[i];

			console.log(`Box count: ${vote.maxPoints}`);
			const boxes = vote.maxPoints;
			const boxSpacing = (pointsWidth - (boxes + 1) * boxWidth) / boxes;
			const boxOffsets: number[] = [];
			for (let j = 0; j <= boxes; j++) {
				boxOffsets.push(candidateWidth + j * (boxWidth + boxSpacing));
			}

			const group = objectGroup();

			if (candidate.minSpot !== lastMinSpot) {

				const theSpot = candidate.minSpot ?? 1;

				const innerGroup = objectGroup();

				const text = textGroup
					.get("fromListenplatz")
					.set("spot", theSpot).get();

				innerGroup
					.addFlow(centeredText(text, assets.bold.atSize(fontSizes.subInstruction), black, vector2d(candidateWidth, 5)))
					.addFlow(whitespace(vector2d(0, 3)));

				const no = textGroup.get("no").get();

				for (let j = 0; j <= boxes; j++) {
					const text = j === 0 ? "0 | " + no : `${j}`;

					innerGroup.addRelative(
						centeredText(text, assets.regular.atSize(fontSizes.subInstruction), black, vector2d(boxWidth, 5)),
						vector2d(boxOffsets[j], 0)
					);
				}

				group.addFlow(innerGroup);

				lastMinSpot = candidate.minSpot ?? 1;
			}

			const outerGroup = objectGroup();

			outerGroup
				.addFlow(multilineText(renderBallotCandidateName(candidate), assets.regular.atSize(fontSizes.candidate), black, candidateWidth))
				.addRelative(whitespace(vector2d(0, 9)), root());

			for (let j = 0; j <= boxes; j++) {
				outerGroup.addRelative(strokeRect(vector2d(boxWidth, boxWidth), 1, black),
					vector2d(boxOffsets[j], 0));
				const name = renderBallotCandidateName(candidate);
				if (vote.prefilled && vote.prefilled[name] === j) {
					outerGroup.addRelative(
						cross(vector2d(boxWidth / 2, boxWidth / 2), boxWidth / 3, black, 1),
						vector2d(boxOffsets[j], 0)
					);
				}
			}

			group.addFlow(outerGroup);

			groups.push(group);
		}

		return groups;
	}

	voteBodyVew20(args: VoteBodyArgs<BallotVoteVEW20>): PdfObjectGroup[] {
		const { vote, writer, assets } = args;
		const fontSizes = this.fontSizes();
		const maxWidth = writer.safeGeometryWidth();
		const { black } = this.colors();

		const candidates = this.sortCandidates(vote.candidates);

		const out: PdfObjectGroup[] = [];

		const boxSize = 4;
		const boxGap = 5;

		for (const candidate of candidates) {
			out.push(objectGroup()
				.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(boxGap, 0.7))
				.addRelative(multilineText(renderBallotCandidateName(candidate), assets.regular.atSize(fontSizes.candidate), black, maxWidth),
					vector2d(boxGap * 2 + boxSize, 0))
				.addFlow(whitespace(vector2d(0, boxSize + boxGap))));
		}

		return out;
	}

	voteBodyYN(args: VoteBodyArgs<BallotVoteYN>): PdfObjectGroup[] {

		const fontSizes = this.fontSizes();

		const { vote, writer, assets } = args;
		const maxWidth = writer.safeGeometryWidth();
		const { black } = this.colors();

		const candidates = this.sortCandidates(vote.candidates);

		const groups: PdfObjectGroup[] = [];

		const availableForBoxes = maxWidth / 2;
		const nameWidth = availableForBoxes - 10;
		const nameOffsetLeft = 5;
		const textWidth = 20;
		const boxSize = 4;
		const boxGap = 5;

		const totalObjectWidth = textWidth + boxSize * 2 + boxGap * 4;

		const leftBoxX = (maxWidth - availableForBoxes) + (availableForBoxes - totalObjectWidth) / 2;
		const leftTextX = leftBoxX + boxSize + boxGap;
		const rightBoxX = leftTextX + textWidth + boxGap;
		const rightTextX = rightBoxX + boxSize + boxGap;

		for (const candidate of candidates) {

			const candidateName = renderBallotCandidateName(candidate);

			const group = objectGroup()
				.addRelative(multilineText(candidateName, assets.regular.atSize(fontSizes.candidate), black, nameWidth), vector2d(nameOffsetLeft, 0))
				.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(leftBoxX, 0))
				.addRelative(multilineText("Ja", assets.regular.atSize(fontSizes.candidate), black, textWidth), vector2d(leftTextX, 0))
				.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(rightBoxX, 0))
				.addRelative(multilineText("Nein", assets.regular.atSize(fontSizes.candidate), black, textWidth), vector2d(rightTextX, 0))
				.addFlow(whitespace(vector2d(0, boxSize + boxGap)));

			groups.push(group);
		}


		return groups;
	}

	fontSizes() {
		return {
			title: 20,
			subTitle: 12,
			instruction: 10,
			subInstruction: 9,
			explainer: 8,
			candidate: 10,
			note: 10
		};
	}

	voteBodyGo8(args: VoteBodyArgs<BallotVoteGO8>): PdfObjectGroup[] {
		const { vote, writer, assets } = args;
		const maxWidth = writer.safeGeometryWidth();
		const fontSizes = this.fontSizes();
		const { black } = this.colors();

		const textWidth = 20;
		const boxSize = 4;
		const boxGap = 5;

		const totalObjectWidth = textWidth + boxSize * 2 + boxGap * 4;

		const leftBoxX = (maxWidth - totalObjectWidth) / 2;
		const leftTextX = leftBoxX + boxSize + boxGap;
		const rightBoxX = leftTextX + textWidth + boxGap;
		const rightTextX = rightBoxX + boxSize + boxGap;

		return [objectGroup()
			.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(boxGap, 0.5))
			.addRelative(multilineText(this.resolveText(vote, "yes"), assets.regular.atSize(fontSizes.candidate), black, maxWidth),
				vector2d(boxGap * 2 + boxSize, 0))
			.addFlow(whitespace(vector2d(0, boxSize + boxGap))),
			objectGroup()
				.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(boxGap, 0.5))
				.addRelative(multilineText(this.resolveText(vote, "no"), assets.regular.atSize(fontSizes.candidate), black, maxWidth),
					vector2d(boxGap * 2 + boxSize, 0))
				.addFlow(whitespace(vector2d(0, boxSize + boxGap)))
		];

		//
		// const group = objectGroup()
		// 	.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(leftBoxX, 0))
		// 	.addRelative(multilineText("Ja", assets.regular.atSize(10), black, textWidth), vector2d(leftTextX, 0))
		// 	.addRelative(strokeRect(vector2d(boxSize, boxSize), 1, black), vector2d(rightBoxX, 0))
		// 	.addRelative(multilineText("Nein", assets.regular.atSize(10), black, textWidth), vector2d(rightTextX, 0))
		// 	.addFlow(whitespace(vector2d(0, boxSize + boxGap * 2)));
		//
		// return [group];
	}

	voteBodyDl38(args: VoteBodyArgs<BallotVoteDL38>): PdfObjectGroup[] {

		const { vote, writer, assets } = args;
		const candidates = this.sortCandidates(vote.candidates);
		const fontSizes = this.fontSizes();
		const maxWidth = writer.safeGeometryWidth();
		const textGroup = this.options.textProvider.group("ballot").sub("dl38");

		const candidateWidth = maxWidth * (1 / 3);
		const pointsWidth = candidateWidth * 2;
		const boxWidth = 4;

		const { black } = this.colors();

		const groups: PdfObjectGroup[] = [];

		const titleGroup = objectGroup();

		const no = textGroup.get("no").get();
		const boxes = 10;
		const boxSpacing = (pointsWidth - (boxes + 1) * boxWidth) / boxes;
		const boxOffsets: number[] = [];
		for (let j = 0; j <= boxes; j++) {
			boxOffsets.push(candidateWidth + j * (boxWidth + boxSpacing));
		}

		for (let j = 0; j <= boxes; j++) {
			const text = j === 0 ? "0 | " + no : `${j}`;

			titleGroup.addRelative(
				centeredText(text, assets.regular.atSize(fontSizes.subInstruction), black, vector2d(boxWidth, 5)),
				vector2d(boxOffsets[j], 0)
			);
		}

		titleGroup.addFlow(whitespace(vector2d(0, 8)));

		groups.push(titleGroup);

		for (let i = 0; i < candidates.length; i++) {

			const candidate = candidates[i];


			const group = objectGroup();

			group
				.addFlow(multilineText(renderBallotCandidateName(candidate), assets.regular.atSize(fontSizes.candidate), black, candidateWidth))
				.addRelative(whitespace(vector2d(0, 9)), root());

			for (let j = 0; j <= boxes; j++) {
				group.addRelative(strokeRect(vector2d(boxWidth, boxWidth), 1, black),
					vector2d(boxOffsets[j], 0));

				const name = renderBallotCandidateName(candidate);
				if (vote.prefilled && vote.prefilled[name] === j) {
					group.addRelative(
						cross(vector2d(boxWidth / 2, boxWidth / 2), boxWidth / 3, black, 1),
						vector2d(boxOffsets[j], 0)
					);
				}
			}

			groups.push(group);
		}

		return groups;
	}

	voteBody<T extends BallotVote>(args: VoteBodyArgs<T>): PdfObjectGroup[] {
		switch (args.vote.type) {
			case "ew19":
				return this.voteBodyEW19(args as VoteBodyArgs<BallotVoteEW19>);
			case "sw23":
				return this.voteBodySW23(args as VoteBodyArgs<BallotVoteSW23>);
			case "vew20":
				return this.voteBodyVew20(args as VoteBodyArgs<BallotVoteVEW20>);
			case "yn":
				return this.voteBodyYN(args as VoteBodyArgs<BallotVoteYN>);
			case "go8":
				return this.voteBodyGo8(args as VoteBodyArgs<BallotVoteGO8>);
			case "dl38":
				return this.voteBodyDl38(args as VoteBodyArgs<BallotVoteDL38>);
			default:
				throw new Error("Unknown vote type");
		}
	}

	async renderResultSheetPage(ballot: Ballot): Promise<ArrayBuffer> {
		const document = await this.options.pdfDocumentManager.create();
		const assets = await this.addAssets(document);
		const fontSizes = this.fontSizes();

		const geometry = BallotRendererImpl.toGeometry("A4");
		const writer = pdfVirtualWriter(geometry.padding(10, 15, 10, 15));
		const { black, lightGray } = this.colors();

		// draw header
		const maxWidth = writer.safeGeometryWidth();

		const tg = this.options.textProvider.group("result");


		const header = objectGroup()
			.addFlow(multilineText(tg.get("title")
				.set("eventName", ballot.eventName)
				.get(), assets.regular.atSize(fontSizes.title), black, maxWidth))
			.addFlow(whitespace(vector2d(0, 5)))
			.addFlow(multilineText(tg.get("disclaimer").get(), assets.regular.atSize(fontSizes.note), black, maxWidth))
			.addFlow(whitespace(vector2d(0, 2.5)))
			.addFlow(multilineText(tg.get("disclaimer2")
				.set("secret", ballot.secret)
				.get(), assets.regular.atSize(fontSizes.note), black, maxWidth))
			.addFlow(whitespace(vector2d(0, 5)));

		writer.addObject(header);

		const candidateNameWidth = maxWidth * 0.5;
		const offsetLeft = 10;
		const lineGap = 3;
		const lineWidth = 15;
		const l1Cd = offsetLeft;
		const l1Line = l1Cd + candidateNameWidth;
		const l1LineEnd = l1Line + lineWidth;
		const l1Stimmen = l1Line + lineWidth + lineGap;
		const l1TextWidth = maxWidth - l1Stimmen;

		const l2CandidateNameWidth = maxWidth * 0.4;
		const l2TextWidth = (maxWidth - (offsetLeft + l2CandidateNameWidth + lineWidth * 3 + lineGap * 5)) / 3;
		const l2Cd = offsetLeft;
		const l2LineYesStart = l2Cd + l2CandidateNameWidth;
		const l2LineYesEnd = l2LineYesStart + lineWidth;
		const l2TextStart = l2LineYesEnd + lineGap;
		const l2LineNoStart = l2TextStart + l2TextWidth + lineGap;
		const l2LineNoEnd = l2LineNoStart + lineWidth;
		const l2TextNoStart = l2LineNoEnd + lineGap;
		const l2LineAbsStart = l2TextNoStart + l2TextWidth + lineGap;
		const l2LineAbsEnd = l2LineAbsStart + lineWidth;
		const l2TextAbsStart = l2LineAbsEnd + lineGap;

		const lineHeight = 4;

		function l2RenderLine(texts: string[]) {
			const ng = objectGroup();

			ng
				.addRelative(multilineText(texts[0], assets.regular.atSize(fontSizes.candidate), black, l2CandidateNameWidth), vector2d(l2Cd, 0))
				.addRelative(line(vector2d(l2LineYesStart, lineHeight), vector2d(l2LineYesEnd, lineHeight), black, 1), root())
				.addRelative(multilineText(texts[1], assets.regular.atSize(fontSizes.candidate), black, l2TextWidth), vector2d(l2TextStart, 0))
				.addRelative(line(vector2d(l2LineNoStart, lineHeight), vector2d(l2LineNoEnd, lineHeight), black, 1), root())
				.addRelative(multilineText(texts[2], assets.regular.atSize(fontSizes.candidate), black, l2TextWidth), vector2d(l2TextNoStart, 0))
				.addRelative(line(vector2d(l2LineAbsStart, lineHeight), vector2d(l2LineAbsEnd, lineHeight), black, 1), root())
				.addRelative(multilineText(texts[3], assets.regular.atSize(fontSizes.candidate), black, l2TextWidth), vector2d(l2TextAbsStart, 0))
				.addFlow(whitespace(vector2d(0, 2.5)));

			writer.addObject(objectGroup()
				.addFlow(whitespace(vector2d(0, 5)))
				.addFlow(ng));
		}

		function l1RenderLine(text: string, name?: string) {

			const ng = objectGroup();

			if (name)
				ng.addRelative(multilineText(name, assets.regular.atSize(fontSizes.candidate), black, maxWidth), vector2d(l1Cd, 0));

			ng
				.addRelative(line(vector2d(l1Line, lineHeight), vector2d(l1LineEnd, lineHeight), black, 1), root())
				.addRelative(multilineText(text, assets.regular.atSize(fontSizes.candidate), black, l1TextWidth), vector2d(l1Stimmen, 0))
				.addFlow(whitespace(vector2d(0, 2.5)));

			writer.addObject(objectGroup()
				.addFlow(whitespace(vector2d(0, 5)))
				.addFlow(ng));
		}

		function abstentionsInvalidAndTotal() {
			l1RenderLine(tg.get("abstentions").get());
			l1RenderLine(tg.get("invalid").get());
			l1RenderLine(tg.get("total").get());
		}

		for (const vote of ballot.votes) {

			const group = objectGroup();

			group.addFlow(multilineText(tg.get("voteTitle")
				.set("what", vote.what).get(), assets.regular.atSize(fontSizes.subTitle), black, maxWidth))
				.addFlow(whitespace(vector2d(0, 2.5)))
				.addFlow(multilineText(this.resolveText(vote, "explainer"),
					assets.regular.atSize(fontSizes.explainer), black, maxWidth))
				.addFlow(whitespace(vector2d(0, 2.5)));

			writer.addObject(group);

			switch (vote.type) {
				case "sw23":
					writer.addObject(objectGroup()
						.addFlow(multilineText(tg.get("unsupported").get(), assets.regular.atSize(fontSizes.note), black, maxWidth)));
					break;
				case "vew20":
				case "ew19":
					for (const candidate of vote.candidates) {
						l1RenderLine(tg.get("votes").get(), renderBallotCandidateName(candidate));
					}
					abstentionsInvalidAndTotal();
					break;
				case "go8":
					l1RenderLine(tg.get("yesVotes").get());
					l1RenderLine(tg.get("noVotes").get());
					abstentionsInvalidAndTotal();
					break;
				case "dl38":
					for (const candidate of vote.candidates) {
						l2RenderLine([renderBallotCandidateName(candidate), tg.get("null").get(),
							tg.get("points").get(), tg.get("invalidShort").get()]);
					}
					l1RenderLine(tg.get("invalid").get());
					l1RenderLine(tg.get("total").get());
					break;
				case "yn":
					for (const candidate of vote.candidates) {
						l2RenderLine([renderBallotCandidateName(candidate), tg.get("yes").get(),
							tg.get("no").get(), tg.get("absShort").get()]);
					}
					l1RenderLine(tg.get("invalid").get());
					l1RenderLine(tg.get("total").get());
					break;
			}

			writer.addObject(objectGroup()
				.addFlow(whitespace(vector2d(0, 7.5)))
				.addFlow(objectGroup()
					.addRelative(line(vector2d(0, 0), vector2d(maxWidth, 0), lightGray, 1), root()))
				.addFlow(whitespace(vector2d(0, 5))));
		}

		// signature block
		const nSignatures = ballot.zkMembers.length > 2 ? 3 : ballot.zkMembers.length + 1;
		const signatureGap = 10;
		const signatureLength = (maxWidth - (nSignatures - 1) * signatureGap) / nSignatures;

		const signatureBlock = objectGroup();

		for (let i = 0; i < nSignatures; i++) {
			const offsetLeft = i * (signatureLength + signatureGap);
			signatureBlock
				.addRelative(line(vector2d(offsetLeft, 0), vector2d(offsetLeft + signatureLength, 0), black, 1), root());
		}

		const namesBlock = objectGroup();

		for (let i = 0; i < nSignatures; i++) {
			const zkMember = i === 0 ? ballot.zkLead + " (Leitung)" : ballot.zkMembers[i - 1];
			const offsetLeft = i * (signatureLength + signatureGap);
			namesBlock
				.addRelative(multilineText(zkMember, assets.regular.atSize(fontSizes.note), black, signatureLength), vector2d(offsetLeft, 0));
		}

		writer.addObject(objectGroup()
			.addFlow(multilineText(tg.get("signatures")
				.set("date", new Date().toLocaleDateString("de-DE"))
				.set("location", ballot.location)
				.get(), assets.regular.atSize(fontSizes.note), black, maxWidth))
			.addFlow(whitespace(vector2d(0, 25)))
			.addFlow(signatureBlock)
			.addFlow(whitespace(vector2d(0, 1)))
			.addFlow(namesBlock)
		);

		writer.complete(document);

		return await this.options.pdfDocumentManager.exportAsArrayBuffer(document);

	}
}
